import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useLocation } from 'react-router-dom'

import { useDisableMainScroll } from '../../hooks/useDisableMainScroll'
import { MenuSection, MenuSection as MenuSectionType } from '../nav/menuSections/useMenuSections'

interface MenuContextValue {
  isMenuOpen: boolean
  expandedSections: string[]
  isScreenMenuOpen?: boolean
  menuItems: MenuSection[]
  actionItems: MenuSection[]
  toggleSectionExpand: (sectionId: string) => void
  expandSection: (sectionId: string) => void
  collapseSection: (sectionId: string) => void
  openMenu: () => void
  closeMenu: () => void
  toggleMenu: () => void
  setMenuItems: (items: MenuSection[]) => void
  setActionItems: (items: MenuSection[]) => void
  setScreenMenuOpen: (isOpen: boolean) => void
}

const MenuContext = createContext<MenuContextValue | undefined>(undefined)

export const MenuProvider: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const { disableMainScroll, enableMainScroll } = useDisableMainScroll()
  const [isMenuOpen, setIsOpen] = useState(false)
  const location = useLocation()
  const [expandedSections, setExpandedSections] = useState<string[]>([])
  const [menuItems, setMenuItems] = useState<MenuSection[]>([])
  const [actionItems, setActionItems] = useState<MenuSection[]>([])
  const [isScreenMenuOpen, setScreenMenuOpen] = useState(false)

  useEffect(() => {
    if (isMenuOpen) {
      disableMainScroll()
    } else {
      enableMainScroll()
    }
  }, [isMenuOpen, disableMainScroll, enableMainScroll])

  const closeMenu = useCallback(() => {
    setExpandedSections([])
    setIsOpen(false)
  }, [setExpandedSections, setIsOpen])

  useEffect(() => {
    if (isMenuOpen) {
      closeMenu()
    }
    if (isScreenMenuOpen) {
      setScreenMenuOpen(false)
    }
  }, [location.pathname])

  const expandSection = useCallback((sectionId: string) => {
    setExpandedSections((prev) => (prev.includes(sectionId) ? prev : [...prev, sectionId]))
  }, [])

  const collapseSection = useCallback((sectionId: string) => {
    setExpandedSections((prev) => prev.filter((id) => id !== sectionId))
  }, [])

  const checkSectionForPath = useCallback(
    (sections: MenuSectionType[], path: string, parentId?: string) => {
      sections.forEach((section) => {
        if (parentId && section.path && path.includes(section.path)) {
          return expandSection(parentId)
        }
        if (section.children) {
          return checkSectionForPath(section.children, path, section.id)
        }
      })
    },
    [expandSection]
  )

  const openMenu = useCallback(() => {
    checkSectionForPath(menuItems, location.pathname)
    setIsOpen(true)
  }, [checkSectionForPath, menuItems, location.pathname])

  const toggleMenu = useCallback(() => {
    if (!isMenuOpen) {
      checkSectionForPath(menuItems, location.pathname)
    }
    setIsOpen((prev) => !prev)
  }, [checkSectionForPath, isMenuOpen, menuItems, location.pathname])

  const toggleSectionExpand = useCallback((sectionId: string) => {
    setExpandedSections((prev) =>
      prev.includes(sectionId) ? prev.filter((id) => id !== sectionId) : [...prev, sectionId]
    )
  }, [])

  return (
    <MenuContext.Provider
      value={{
        isMenuOpen,
        expandedSections,
        menuItems,
        actionItems,
        isScreenMenuOpen,
        toggleSectionExpand,
        openMenu,
        closeMenu,
        toggleMenu,
        expandSection,
        collapseSection,
        setMenuItems,
        setActionItems,
        setScreenMenuOpen,
      }}
    >
      {children}
    </MenuContext.Provider>
  )
}

export const useMenu = (): MenuContextValue => {
  const ctx = useContext(MenuContext)

  if (!ctx) {
    throw new Error('useMenu must be inside <MenuProvider>')
  }
  return ctx
}
